import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function AgentPage() {
  // 設定代理回饋頁 Meta
  usePageMeta({
    title: '代理回饋制度 | laigame手遊代儲',
    description: 'laigame代理回饋制度說明，邀請好友註冊消費即可享有1%持續性消費回饋，累積5位新會員即可開始獲得購物金或現金回饋，無限暢玩手遊世界。',
    keywords: '代理回饋,推薦獎勵,購物金,現金回饋,推薦代碼,分潤制度,laigame',
    ogTitle: '代理回饋制度 | laigame手遊代儲',
    ogDescription: 'laigame代理回饋制度，邀請好友註冊消費即可享有1%持續性消費回饋，累積5位新會員即可開始獲得回饋'
  });

  const [activeTab, setActiveTab] = useState<'shopping' | 'cash'>('shopping');

  useEffect(() => {
    // Add WebPage + BreadcrumbList Schema
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "代理回饋制度",
      "description": "laigame 代理回饋制度說明，邀請好友註冊消費即可享有持續性的消費回饋，無限暢玩手遊世界",
      "url": `${siteUrl}/agent`,
      "isPartOf": {
        "@type": "WebSite",
        "name": "laigame手遊代儲",
        "url": siteUrl
      }
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${siteUrl}/agent#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首頁",
          "item": `${siteUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "代理回饋制度",
          "item": `${siteUrl}/agent`
        }
      ]
    };

    const webpageScript = document.createElement('script');
    webpageScript.type = 'application/ld+json';
    webpageScript.id = 'agent-webpage-schema';
    webpageScript.text = JSON.stringify(webpageSchema);
    document.head.appendChild(webpageScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'agent-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      ['agent-webpage-schema', 'agent-breadcrumb-schema'].forEach(sid => {
        const el = document.getElementById(sid);
        if (el) document.head.removeChild(el);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />

      {/* Hero Banner */}
      <section className="relative bg-gradient-to-br from-[#1a1f5e] via-[#2563eb] to-[#1e40af] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <i className="ri-vip-crown-line text-2xl"></i>
              <span className="text-sm font-semibold">重磅推出</span>
            </div>
            <h1 className="text-5xl font-bold mb-6">代理回饋制度</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              人人都能化身推薦大使！透過專屬推薦代碼，邀請好友註冊消費，即可享有持續性的消費回饋，無限暢玩手遊世界！
            </p>
          </div>
        </div>
      </section>

      {/* 專屬推薦代碼說明 */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <i className="ri-user-star-line text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">專屬推薦代碼</h2>
          </div>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            只要是 laigame 網站註冊會員，都能擁有一個專屬推薦代碼。當朋友使用您的代碼註冊新會員並消費，累積新客戶達 <strong className="text-blue-600">5 位以上</strong>後，您就能開始獲得好友消費金額的 <strong className="text-blue-600">1% 回饋</strong>！
          </p>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <i className="ri-error-warning-line text-2xl text-amber-600 mt-1"></i>
              <div>
                <h3 className="text-lg font-bold text-amber-800 mb-2">重要提醒</h3>
                <p className="text-amber-700 leading-relaxed">
                  需達成 <strong>5 位以上新會員且皆有消費後</strong>，才會開始計算分潤唷！滿足條件之前的消費不列入計算。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 回饋計算方式 */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <i className="ri-percent-line text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">1% 回饋計算方式</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
              <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-user-add-line text-2xl text-white"></i>
              </div>
              <h3 className="text-center font-bold text-gray-800 mb-2">步驟 1</h3>
              <p className="text-center text-gray-600 text-sm">邀請好友使用您的專屬代碼註冊</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
              <div className="w-14 h-14 bg-purple-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-shopping-cart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-center font-bold text-gray-800 mb-2">步驟 2</h3>
              <p className="text-center text-gray-600 text-sm">累積 5 位新會員且皆有消費</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
              <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-center font-bold text-gray-800 mb-2">步驟 3</h3>
              <p className="text-center text-gray-600 text-sm">開始獲得好友消費金額 1% 回饋</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4 text-center">計算範例</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <p className="text-sm text-white/80 mb-2">好友當月消費總額</p>
                <p className="text-4xl font-bold mb-1">NT$ 100,000</p>
                <p className="text-sm text-white/70">5 位好友各消費 NT$ 20,000</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <p className="text-sm text-white/80 mb-2">您的回饋金額（1%）</p>
                <p className="text-4xl font-bold mb-1">NT$ 1,000</p>
                <p className="text-sm text-white/70">100,000 × 1% = 1,000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 月結回饋方案 */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <i className="ri-gift-line text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">月結回饋方案</h2>
          </div>

          {/* 方案切換 */}
          <div className="flex gap-3 mb-8 bg-gray-100 p-1 rounded-xl max-w-md mx-auto">
            <button
              onClick={() => setActiveTab('shopping')}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'shopping'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-shopping-bag-line mr-2"></i>
              購物金方案
            </button>
            <button
              onClick={() => setActiveTab('cash')}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'cash'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-bank-card-line mr-2"></i>
              現金提領方案
            </button>
          </div>

          {/* 購物金方案 */}
          {activeTab === 'shopping' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">購物金自動發放</h3>
                </div>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  當月結推薦消費回饋 <strong className="text-amber-600">低於 NT$ 5,000</strong> 時（依 1% 計算，也就是一個月消費金額在 50 萬元以下者），系統將直接發送購物金給代碼擁有者。
                </p>
                
                <div className="bg-white rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">自動發放</h4>
                      <p className="text-gray-600 text-sm">系統自動將回饋金額轉為購物金</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">無使用期限</h4>
                      <p className="text-gray-600 text-sm">購物金永久有效，隨時可用於結帳消費</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">彈性使用</h4>
                      <p className="text-gray-600 text-sm">結帳時可選擇使用購物金作為付費方式</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 現金提領方案 */}
          {activeTab === 'cash' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <i className="ri-bank-card-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">現金提領選項</h3>
                </div>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  若月結推薦消費回饋 <strong className="text-green-600">大於或等於 NT$ 5,000</strong> 時，玩家可選擇回饋購物金或提領現金。
                </p>
                
                <div className="bg-white rounded-lg p-6 space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">雙重選擇</h4>
                      <p className="text-gray-600 text-sm">可選擇購物金或現金提領</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">需綁定銀行帳戶</h4>
                      <p className="text-gray-600 text-sm">選擇現金回饋需先綁定銀行帳戶</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">每月 10 號前選擇</h4>
                      <p className="text-gray-600 text-sm">選擇期限為當月 10 號，逾期自動發購物金</p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-alert-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-red-800 mb-2">銀行帳戶綁定注意事項</h4>
                      <ul className="space-y-2 text-red-700 text-sm">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line mt-0.5"></i>
                          <span>帳戶綁定後 <strong>無法隨意修改</strong></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line mt-0.5"></i>
                          <span>銀行帳戶跟會員帳號只能 <strong>一對一使用</strong></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line mt-0.5"></i>
                          <span>請會員務必注意此項規定，綁定前請再三確認</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 選擇期限說明 */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-8 md:p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <i className="ri-calendar-check-line text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold">回饋方案選擇期限</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold">選擇時限</h3>
              </div>
              <p className="text-white/90 leading-relaxed">
                回饋方案選擇時限為期一周，最後期限為 <strong className="text-yellow-300">當月 10 號</strong>。
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-error-warning-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold">逾期處理</h3>
              </div>
              <p className="text-white/90 leading-relaxed">
                若時間截止玩家還未進行選擇，系統將 <strong className="text-yellow-300">自動發放購物金</strong>，一旦發放為購物金後不能再進行更改。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 注意事項 */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
              <i className="ri-information-line text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">重要注意事項</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-5 bg-red-50 border-l-4 border-red-500 rounded-lg">
              <i className="ri-close-circle-line text-2xl text-red-600 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">同業使用限制</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  如發現使用代碼者為代儲同業，本公司可無條件取消該帳戶購物金權利。
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <i className="ri-gamepad-line text-2xl text-blue-600 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">適用範圍</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  本活動僅適用於手遊遊戲，不適用於海外點數代付服務及台灣點數卡。
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 bg-amber-50 border-l-4 border-amber-500 rounded-lg">
              <i className="ri-file-edit-line text-2xl text-amber-600 mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">活動修改權</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  本活動主辦單位保有隨時修改及終止本活動之權利，如有任何變更內容或詳細注意事項將公布於本網頁，恕不另行通知。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 區塊 */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-12 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-user-star-line text-4xl"></i>
            </div>
            <h2 className="text-4xl font-bold mb-4">立即加入會員</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              註冊成為 laigame 會員，獲取專屬推薦代碼，開始您的推薦大使之旅！
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/account"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line text-xl"></i>
                立即註冊
              </a>
              <a
                href="/faq"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-question-line text-xl"></i>
                查看常見問題
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}